import { useMemo, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TrendingDown, TrendingUp, BarChart3 } from 'lucide-react';

/**
 * ScenarioWaterfallChart - Professional waterfall chart for cost impact visualization
 * 
 * Design Elements:
 * 1. Baseline Bar (Leftmost): Gray, anchored at 0
 * 2. Intermediate Bars (Floating): Green for reduction, Red for increase
 * 3. Net Total Bar (Rightmost): Dark gray, anchored at 0
 * 4. NET IMPACT Badge (Top Right)
 */

// Colors
const COLORS = {
  baseline: '#6B7280',      // Gray
  netTotal: '#374151',      // Dark Gray
  reduction: '#10B981',     // Green
  increase: '#EF4444',      // Red
  connector: '#D1D5DB',     // Light Gray
};

// Component labels mapping - defined outside component
const COMPONENT_LABELS = {
  base_material: 'Material',
  material_index: 'Mat. Index',
  labor_index: 'Labor',
  overhead: 'Overhead',
  logistics: 'Logistics',
  fx_impact: 'FX Impact',
  tariff: 'Tariff',
  surcharge: 'Surcharges',
  other: 'Other',
};

// Format currency helper - defined outside component
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0';
  const value = Math.abs(cents) / 100;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const formatDeltaCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0';
  const sign = cents < 0 ? '-' : '+';
  const value = Math.abs(cents) / 100;
  if (value >= 1000000) return `${sign}$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${sign}$${(value / 1000).toFixed(1)}K`;
  return `${sign}$${value.toFixed(0)}`;
};

// Custom tooltip component - defined outside main component
const WaterfallTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  
  return (
    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3">
      <p className="font-semibold text-gray-900 mb-1">{data.name}</p>
      {data.type === 'baseline' || data.type === 'netTotal' ? (
        <p className="text-gray-700">
          Total: <span className="font-mono font-medium">{formatCurrency(data.displayValue)}</span>
        </p>
      ) : (
        <>
          <p className={`font-medium ${data.type === 'reduction' ? 'text-green-700' : 'text-red-700'}`}>
            Delta: <span className="font-mono">{formatDeltaCurrency(data.displayValue)}</span>
            <span className="text-sm ml-1">({data.deltaPercent?.toFixed(1)}%)</span>
          </p>
          <div className="text-xs text-gray-500 mt-1 pt-1 border-t border-gray-100">
            <p>Baseline: {formatCurrency(data.baselineValue)}</p>
            <p>Comparison: {formatCurrency(data.currentValue)}</p>
          </div>
        </>
      )}
    </div>
  );
};

// Custom legend component - defined outside main component
const WaterfallLegend = () => (
  <div className="flex items-center justify-center gap-6 mt-4 flex-wrap">
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.baseline }} />
      <span className="text-sm text-gray-600">Baseline Total</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.reduction }} />
      <span className="text-sm text-gray-600">Cost Reduction</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.increase }} />
      <span className="text-sm text-gray-600">Cost Increase</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.netTotal }} />
      <span className="text-sm text-gray-600">Net Total</span>
    </div>
  </div>
);

export default function ScenarioWaterfallChart({ 
  data, 
  baselineScenario, 
  comparisonScenario 
}) {
  // Transform data for waterfall chart
  const chartData = useMemo(() => {
    if (!data || !data.component_impacts) return [];

    const { component_impacts } = data;
    
    // Calculate totals
    const baselineTotal = component_impacts.reduce(
      (sum, c) => sum + (c.baseline_amount_cents ?? c.baseline_cents ?? 0),
      0
    );
    const comparisonTotal = component_impacts.reduce(
      (sum, c) => sum + (c.current_amount_cents ?? c.current_cents ?? 0),
      0
    );
    
    // Filter out zero delta components and sort by absolute impact
    const nonZeroComponents = component_impacts
      .filter(c => c.delta_cents !== 0)
      .sort((a, b) => Math.abs(b.delta_cents) - Math.abs(a.delta_cents));
    
    // Build waterfall data
    const result = [];
    let runningTotal = baselineTotal;
    
    // Baseline bar
    result.push({
      name: 'Baseline',
      value: baselineTotal,
      displayValue: baselineTotal,
      start: 0,
      end: baselineTotal,
      type: 'baseline',
      tooltip: `${baselineScenario?.name || 'Baseline'} Total`,
    });
    
    // Component delta bars (floating)
    nonZeroComponents.forEach((component) => {
      const label = COMPONENT_LABELS[component.component_type] || component.component_type;
      const delta = component.delta_cents;
      const newTotal = runningTotal + delta;
      
      result.push({
        name: label,
        value: Math.abs(delta),
        displayValue: delta,
        start: delta > 0 ? runningTotal : newTotal,
        end: delta > 0 ? newTotal : runningTotal,
        type: delta < 0 ? 'reduction' : 'increase',
        tooltip: `${label}: ${delta < 0 ? '-' : '+'}$${(Math.abs(delta) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        baselineValue: component.baseline_amount_cents ?? component.baseline_cents ?? 0,
        currentValue: component.current_amount_cents ?? component.current_cents ?? 0,
        deltaPercent: component.delta_percent,
      });
      
      runningTotal = newTotal;
    });
    
    // Net total bar
    result.push({
      name: 'Net Total',
      value: comparisonTotal,
      displayValue: comparisonTotal,
      start: 0,
      end: comparisonTotal,
      type: 'netTotal',
      tooltip: `${comparisonScenario?.name || 'Comparison'} Total`,
    });
    
    return result;
  }, [data, baselineScenario, comparisonScenario]);

  // Calculate net impact
  const netImpact = useMemo(() => {
    if (!data || !data.summary) return null;
    return {
      cents: data.summary.net_delta_cents,
      percent: data.summary.net_delta_percent,
      isFavorable: data.summary.is_favorable,
    };
  }, [data]);

  // Y-axis formatter
  const yAxisFormatter = useCallback((value) => formatCurrency(value), []);

  if (!data || !chartData.length) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">No comparison data available. Please select two scenarios to compare.</p>
        </CardContent>
      </Card>
    );
  }

  // Find max value for Y axis
  const maxValue = Math.max(...chartData.map(d => d.end)) * 1.1;

  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Cost Impact Waterfall
            </CardTitle>
            <CardDescription>
              {baselineScenario?.name || 'Baseline'} {'->'} {comparisonScenario?.name || 'Comparison'}
            </CardDescription>
          </div>
          
          {/* NET IMPACT Badge */}
          {netImpact && (
            <div className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              netImpact.isFavorable 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {netImpact.isFavorable ? (
                <TrendingDown className="h-5 w-5" />
              ) : (
                <TrendingUp className="h-5 w-5" />
              )}
              <div className="text-right">
                <p className="text-xs font-medium uppercase">Net Impact</p>
                <p className="text-lg font-bold">
                  {formatDeltaCurrency(netImpact.cents)}
                </p>
                <p className="text-xs">
                  {netImpact.percent > 0 ? '+' : ''}{netImpact.percent?.toFixed(1)}%
                </p>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis 
                tickFormatter={yAxisFormatter}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
                domain={[0, maxValue]}
              />
              <Tooltip content={<WaterfallTooltip />} />
              <ReferenceLine y={0} stroke="#9CA3AF" />
              
              {/* Invisible bar for floating effect (start position) */}
              <Bar 
                dataKey="start" 
                stackId="stack" 
                fill="transparent" 
              />
              
              {/* Visible bar (actual value) */}
              <Bar 
                dataKey="value" 
                stackId="stack" 
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry, index) => {
                  let fill = COLORS.baseline;
                  if (entry.type === 'reduction') fill = COLORS.reduction;
                  else if (entry.type === 'increase') fill = COLORS.increase;
                  else if (entry.type === 'netTotal') fill = COLORS.netTotal;
                  return <Cell key={`cell-${index}`} fill={fill} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Custom Legend */}
        <WaterfallLegend />
        
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-200">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase font-medium">Baseline Total</p>
            <p className="text-xl font-bold text-gray-700 font-mono">
              {formatCurrency(chartData[0]?.displayValue)}
            </p>
            <p className="text-xs text-gray-500">{baselineScenario?.name}</p>
          </div>
          <div className={`text-center p-3 rounded-lg ${
            netImpact?.isFavorable ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <p className="text-xs text-gray-500 uppercase font-medium">Net Change</p>
            <p className={`text-xl font-bold font-mono ${
              netImpact?.isFavorable ? 'text-green-700' : 'text-red-700'
            }`}>
              {formatDeltaCurrency(netImpact?.cents)}
            </p>
            <p className="text-xs text-gray-500">
              {netImpact?.percent > 0 ? '+' : ''}{netImpact?.percent?.toFixed(1)}%
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase font-medium">Net Total</p>
            <p className="text-xl font-bold text-gray-700 font-mono">
              {formatCurrency(chartData[chartData.length - 1]?.displayValue)}
            </p>
            <p className="text-xs text-gray-500">{comparisonScenario?.name}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
